import { PanelLeftClose, SendHorizontal } from 'lucide-react';
import { cleanDisplayText } from '@/utils/display-text';
import { Skeleton } from '@/components/ui';
import { CanvasEmptyState } from '@/components/canvas/CanvasEmptyState';

interface OperatorLoadingSkeletonProps {
  chatWidth?: number;
  chatTitle?: string;
}

/**
 * Layout-matching skeleton shown while a conversation is loading.
 * Fixed shell (chat header, composer, canvas tab bar) renders as the real UI;
 * only the chat thread's message list shows skeleton placeholders, with spacing
 * that mirrors the live conversation layout. The canvas surface area stays
 * empty so it doesn't flash content before the real surfaces mount.
 */
export default function OperatorLoadingSkeleton({
  chatWidth = 600,
  chatTitle = 'Chat',
}: OperatorLoadingSkeletonProps = {}) {
  return (
    <div className="flex h-screen flex-col">
      <div className="flex min-h-0 flex-1">
        {/* Chat panel — fixed shell with real header + composer */}
        <div
          className="flex shrink-0 flex-col border-r border-border bg-background"
          style={{ width: chatWidth }}
        >
          {/* Real header */}
          <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-3">
            <h3
              className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground"
              title={cleanDisplayText(chatTitle)}
            >
              {cleanDisplayText(chatTitle)}
            </h3>
            <button
              type="button"
              aria-label="Collapse chat"
              disabled
              className="rounded-md p-1 text-muted-foreground opacity-60"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>

          {/* Skeleton messages — spacing matches AgentUserMessage / AgentAssistantMessage.
              `--aui-background` matches the live thread viewport so the swap is seamless. */}
          <div className="flex flex-1 flex-col overflow-hidden bg-[hsl(var(--aui-background))] px-6 py-8">
            {/* User message — right-aligned bubble */}
            <div className="flex justify-end">
              <Skeleton className="h-9 w-3/5 rounded-2xl" />
            </div>

            {/* Assistant message — avatar + content */}
            <div className="mt-14 flex items-start gap-4">
              <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
              <div className="flex-1 space-y-3 pt-2.5">
                <Skeleton className="h-3 w-32" />
                <div className="space-y-2 pt-1">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-11/12" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
                <Skeleton className="h-6 w-32 rounded-full" />
              </div>
            </div>

            {/* Assistant message — avatar + content */}
            <div className="mt-14 flex items-start gap-4">
              <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
              <div className="flex-1 space-y-3 pt-2.5">
                <Skeleton className="h-3 w-32" />
                <div className="space-y-2 pt-1">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            </div>
          </div>

          {/* Real composer — matches MentionAwareComposer styling so the swap
              from skeleton → live composer is structural. The outer padding
              mirrors the live thread viewport (`px-4`) + viewport-footer
              (`pb-4`) so the pill lands at the same x-position when the
              skeleton crossfades into the real composer. */}
          <div className="shrink-0 bg-[hsl(var(--aui-background))] px-4 pb-8 pt-3">
            <div className="relative flex h-16 items-center rounded-3xl border border-border bg-muted px-2 shadow-2xl shadow-black/40">
              <span className="flex-grow self-stretch px-2 py-4 text-base leading-7 text-muted-foreground">
                How can I help you today?
              </span>
              <button
                type="button"
                aria-label="Send"
                disabled
                className="my-2 inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-background text-primary shadow shadow-black/30 border-2 border-foreground/25"
              >
                <SendHorizontal className="h-5 w-5 translate-x-px" />
              </button>
            </div>
          </div>
        </div>

        {/* Canvas — same shell as the live panel (h-16 header strip + body)
            so the crossfade is structural no-op; only the icon's pulse drops. */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex h-16 shrink-0 items-center justify-end border-b border-border pr-8" />
          <div className="min-h-0 flex-1">
            <CanvasEmptyState loading />
          </div>
        </div>
      </div>
    </div>
  );
}
